/**
 * Business logic helpers for BulkDeleteModal.
 *
 * All functions are pure (no Vue/Vuex side effects) and unit-testable.
 * The sequential delete loop `executeBulkDelete` accepts ApiService and
 * buildDeleteDraftEndpoint as injected dependencies for easier testing.
 *
 * @module bulkDeleteModal.logic
 */

import { EINVOICE_STATUS } from '@/utils/einvoice.enum'

// ─── Blocked Reason Constants ──────────────────────────────

/**
 * Canonical blocked reason codes and their display labels.
 * Evaluation order: CONTRACT_INVALID → MISSING_SALES_INVOICE_ID → NOT_DRAFT_STATUS
 * (per ADR-04)
 *
 * @type {Readonly<Object>}
 */
export const BULK_DELETE_BLOCKED_REASON = Object.freeze({
  CONTRACT_INVALID: {
    code: 'CONTRACT_INVALID',
    label: 'Loại hóa đơn không hợp lệ',
    description: 'Loại hóa đơn không được hỗ trợ',
  },
  MISSING_SALES_INVOICE_ID: {
    code: 'MISSING_SALES_INVOICE_ID',
    label: 'Thiếu ID hóa đơn',
    description: 'Không tìm thấy salesInvoiceId',
  },
  NOT_DRAFT_STATUS: {
    code: 'NOT_DRAFT_STATUS',
    label: 'Không phải hóa đơn nháp',
    description: 'Chỉ có thể xóa hóa đơn ở trạng thái nháp',
  },
})

// ─── Classification ─────────────────────────────────────────

/**
 * Classify an array of selected invoice rows into eligible and blocked buckets.
 *
 * Evaluation order per ADR-04:
 *  1. contractInvalid === true → CONTRACT_INVALID
 *  2. !salesInvoiceId → MISSING_SALES_INVOICE_ID
 *  3. status !== DRAFT → NOT_DRAFT_STATUS
 *  Otherwise → eligible
 *
 * @param {Array<Object>} items - Raw selected row objects from useBulkSelection
 * @returns {{ eligible: Array<Object>, blocked: Array<Object> }}
 */
export function classifyBulkDeleteItems(items = []) {
  const eligible = []
  const blocked = []

  const normalizedItems = Array.isArray(items) ? items : []

  for (const item of normalizedItems) {
    if (!item || typeof item !== 'object') continue

    if (item.contractInvalid) {
      blocked.push({ ...item, blockedReason: BULK_DELETE_BLOCKED_REASON.CONTRACT_INVALID })
    } else if (!item.salesInvoiceId) {
      blocked.push({ ...item, blockedReason: BULK_DELETE_BLOCKED_REASON.MISSING_SALES_INVOICE_ID })
    } else if (item.status !== EINVOICE_STATUS.DRAFT) {
      blocked.push({ ...item, blockedReason: BULK_DELETE_BLOCKED_REASON.NOT_DRAFT_STATUS })
    } else {
      eligible.push(item)
    }
  }

  return { eligible, blocked }
}

// ─── Sale Type Normalization ────────────────────────────────

/**
 * Normalize saleType to a canonical 'retail' | 'wholesale' string.
 * Throws if the value is neither.
 *
 * @param {string} saleType
 * @returns {'retail' | 'wholesale'}
 * @throws {Error} if saleType is not recognized
 */
export function normalizeBulkDeleteSaleType(saleType) {
  const normalized = typeof saleType === 'string' ? saleType.trim().toLowerCase() : ''
  if (normalized === 'retail' || normalized === 'wholesale') {
    return normalized
  }

  throw new Error('saleType phải là "retail" hoặc "wholesale"')
}

// ─── Error Normalization ────────────────────────────────────

/**
 * Normalize a per-item API error into a canonical error descriptor.
 * Per ADR-05 and SRS FR-09: 400 "already deleted" → isIdempotent = true (treated as success).
 *
 * @param {Error|Object} err - The caught error from ApiService.delete()
 * @returns {{ code: string, message: string, isIdempotent?: boolean }}
 */
export function normalizeDeleteError(err) {
  const status = err?.response?.status || err?.status
  const message =
    err?.response?.data?.message || err?.response?.data?.error || err?.message || ''

  if (status === 400 && String(message).toLowerCase().includes('already deleted')) {
    return { code: 'ALREADY_DELETED', message: 'Đã xóa trước đó', isIdempotent: true }
  }

  if (status === 404) {
    return { code: 'NOT_FOUND', message: 'Không tìm thấy bản nháp', isIdempotent: false }
  }

  if (status === 409) {
    return { code: 'ACTION_BLOCKED', message: 'Trạng thái không cho phép xóa', isIdempotent: false }
  }

  if (status >= 500) {
    return { code: 'SERVER_ERROR', message: 'Lỗi hệ thống, vui lòng thử lại', isIdempotent: false }
  }

  return { code: 'UNKNOWN', message: message || 'Lỗi không xác định', isIdempotent: false }
}

// ─── Overview Stats Builder ─────────────────────────────────

/**
 * Build the overview stats array for BulkDeleteOverview.
 *
 * @param {Object} params
 * @param {number} params.totalSelected
 * @param {number} params.eligibleCount
 * @param {number} params.blockedCount
 * @returns {Array<{ key: string, label: string, value: number, tone: string }>}
 */
export function buildBulkDeleteOverviewStats({ totalSelected = 0, eligibleCount = 0, blockedCount = 0 } = {}) {
  return [
    { key: 'total', label: 'Đã chọn', value: totalSelected, tone: 'neutral' },
    { key: 'eligible', label: 'Hợp lệ', value: eligibleCount, tone: 'success' },
    {
      key: 'blocked',
      label: 'Bị chặn',
      value: blockedCount,
      tone: blockedCount > 0 ? 'warning' : 'muted',
    },
  ]
}

// ─── Footer Summary Builders ────────────────────────────────

/**
 * Build the footer decision summary shown in the review phase.
 *
 * @param {Object} params
 * @param {number} params.eligibleCount - Number of eligible items available to delete
 * @param {number} params.blockedCount
 * @returns {{ title: string, amount: string, blockedHint: string }}
 */
export function buildBulkDeleteDecisionSummary({ eligibleCount = 0, blockedCount = 0 } = {}) {
  return {
    title: `Sẽ xóa ${eligibleCount} hóa đơn nháp`,
    amount: '',
    blockedHint: blockedCount > 0 ? `${blockedCount} hóa đơn bị chặn, không nằm trong lượt này` : '',
  }
}

/**
 * Build the footer summary for result or error phases.
 *
 * @param {'result' | 'error' | 'zeroEligible'} phase
 * @param {{ succeeded: Array, failed: Array } | null} result
 * @returns {{ title: string, amount: string, blockedHint: string }}
 */
export function buildBulkDeleteFooterSummary(phase, result = null) {
  if (phase === 'zeroEligible') {
    return {
      title: 'Chưa có hóa đơn nháp hợp lệ để xóa',
      amount: '',
      blockedHint: '',
    }
  }

  if (phase === 'error') {
    return {
      title: 'Đã xảy ra lỗi trong quá trình xóa',
      amount: 'Rà soát lỗi rồi thử lại sau',
      blockedHint: '',
    }
  }

  if (phase === 'result' && result) {
    return {
      title: 'Đã xử lý xong lượt xóa',
      amount: `Thành công ${result.succeeded.length}, thất bại ${result.failed.length}`,
      blockedHint: '',
    }
  }

  return { title: '', amount: '', blockedHint: '' }
}

// ─── Sequential Delete Execution ───────────────────────────

/**
 * Execute bulk delete sequentially (no parallel) for each eligible item.
 *
 * Per ADR-02: the modal component owns this loop via this helper.
 * The function calls `onProgress` callbacks after each item so the
 * caller can update Vuex state with granular progress.
 *
 * Per ADR-05: 400 "already deleted" → treated as succeeded (idempotent).
 *
 * Failed entries store the original item object under `item` key so
 * retryFailed() in the modal can recover them without re-classification.
 *
 * @param {Array<Object>} items - Eligible items to delete
 * @param {string} saleType - 'retail' | 'wholesale'
 * @param {Object} callbacks
 * @param {Function} callbacks.onProgress - Called with { processed, total, currentItem } before/after each item
 * @param {Function} [callbacks.onItemComplete] - Called after each item is processed (success or fail)
 * @param {Object} [apiService] - Injected ApiService instance (must have .delete(url) method)
 * @param {Function} [buildEndpoint] - Injected endpoint builder function
 * @returns {Promise<{ succeeded: Array<Object>, failed: Array<Object> }>}
 */
export async function executeBulkDelete(
  items = [],
  saleType,
  { onProgress, onItemComplete } = {},
  apiService = null,
  buildEndpoint = null,
) {
  const results = { succeeded: [], failed: [] }
  const normalizedItems = Array.isArray(items) ? items : []
  const total = normalizedItems.length

  const safeOnProgress = typeof onProgress === 'function' ? onProgress : () => {}
  const safeOnItemComplete = typeof onItemComplete === 'function' ? onItemComplete : () => {}

  // Signal start: 0 processed, before first item
  safeOnProgress({ processed: 0, total, currentItem: null })

  for (let i = 0; i < normalizedItems.length; i++) {
    const item = normalizedItems[i]
    const currentItemLabel =
      item.saleInvoiceCode || String(item.salesInvoiceId || '') || `HĐ #${i + 1}`

    // Signal: this item is now being processed
    safeOnProgress({ processed: i, total, currentItem: currentItemLabel })

    try {
      const endpoint = buildEndpoint(item.salesInvoiceId, saleType)
      await apiService.delete(endpoint)
      results.succeeded.push(item)
    } catch (err) {
      const normalizedError = normalizeDeleteError(err)

      if (normalizedError.isIdempotent) {
        // 400 already-deleted → treat as success per ADR-05
        results.succeeded.push(item)
      } else {
        results.failed.push({
          item,                               // Original row for retry recovery
          error: normalizedError,
          salesInvoiceId: item.salesInvoiceId,
          saleInvoiceCode: item.saleInvoiceCode || item.salesInvoiceId,
          buyerName: item.buyerName || '',
        })
      }
    }

    safeOnItemComplete(item, i)
  }

  // Signal completion: all items processed
  safeOnProgress({ processed: total, total, currentItem: null })

  return results
}
